// services/communityAlertsService.js
//
// Single boundary for all community_alerts table reads/writes. Components
// MUST NOT touch the table directly — call these functions instead so
// validation, audit, and the tier-derived defaults stay in one place.
//
// RLS gates admin-only writes at the DB layer; this service adds:
//   - tier-specific validation (Tier 4 reason required, etc.)
//   - tier-derived defaults (pauses_service, dismissible)
//   - the canonical TIERS palette + behavior config
//
// Phase 1 scope. Phase 2 (Hauler banner) reuses getActiveAlerts. Phase 3
// (service-pause enforcement) reads pauses_service. Phase 4 (AI co-pilot)
// extends publishAlert with a draft input. Phase 5 (push + SMS) hooks
// post-publish.

import { supabase } from "./supabaseService";
import { sendSMSNotification } from "./notificationService";

// ── TIERS ──────────────────────────────────────────────────────────────────
// Canonical tier definitions. UI palette + friction + defaults all derive
// from this object. Keep in sync with the prototype HTML; the admin
// composer and the resident banner both read from here so visual
// treatment never drifts between the publish surface and the display
// surface.
export const TIERS = {
  0: {
    key: "info",
    name: "Info",
    desc: "FYI · neighborhood news, expansion announcements, schedule notes",
    bg: "rgba(61,144,217,.10)",
    accent: "#3D90D9",
    text: "#0A1628",
    pillBg: "rgba(61,144,217,.18)",
    pillText: "#1565a8",
    confirmType: "none",          // direct publish
    autoExpireHours: null,        // manual lift only
    pausesService: false,
    dismissible: true,
    confirmWord: null,
  },
  1: {
    key: "warning",
    name: "Warning",
    desc: "Heads up · weather watch, partial-area delays, advance notice",
    bg: "rgba(242,151,39,.10)",
    accent: "#F29727",
    text: "#0A1628",
    pillBg: "rgba(242,151,39,.18)",
    pillText: "#9a5a07",
    confirmType: "none",
    autoExpireHours: 48,
    pausesService: false,
    dismissible: true,
    confirmWord: null,
  },
  2: {
    key: "impact",
    name: "Service Impact",
    desc: "Active disruption · zone closure, route delay, capacity reduction",
    bg: "rgba(242,151,39,.16)",
    accent: "#d97706",
    text: "#0A1628",
    pillBg: "rgba(242,151,39,.22)",
    pillText: "#7c3a00",
    confirmType: "tap",           // one-tap confirm panel
    autoExpireHours: 24,
    pausesService: false,
    dismissible: true,
    confirmWord: null,
  },
  3: {
    key: "pause",
    name: "Service Hold",
    desc: "Platform paused · new bookings blocked, in-flight jobs continue",
    bg: "rgba(217,82,82,.10)",
    accent: "#D95252",
    text: "#0A1628",
    pillBg: "rgba(217,82,82,.18)",
    pillText: "#a32a2a",
    confirmType: "type",          // two-step + type confirm word
    autoExpireHours: null,
    pausesService: true,
    dismissible: false,
    confirmWord: "HOLD",
  },
  4: {
    key: "emergency",
    name: "Emergency",
    desc: "Immediate safety · evacuation, severe weather, security incident",
    bg: "rgba(217,82,82,.18)",
    accent: "#b91c1c",
    text: "#0A1628",
    pillBg: "rgba(217,82,82,.28)",
    pillText: "#7f1d1d",
    confirmType: "type-reason",   // two-step + type word + reason required
    autoExpireHours: null,
    pausesService: false,
    dismissible: false,
    confirmWord: "EMERGENCY",
  },
};

export function tierConfig(tier) {
  return TIERS[tier] || TIERS[0];
}

// ── Read paths ─────────────────────────────────────────────────────────────

/**
 * Active alerts visible to the given audience role. Filters client-side
 * on the audience array (RLS only gates active vs lifted).
 *
 * @param {object} opts
 * @param {string} opts.audience - 'resident' | 'driver' | 'admin'
 * @returns {Promise<Array<object>>} alerts sorted by tier desc, then created_at desc
 */
export async function getActiveAlerts({ audience = "resident" } = {}) {
  const { data, error } = await supabase
    .from("community_alerts")
    .select("*")
    .is("lifted_at", null)
    .order("tier", { ascending: false })
    .order("created_at", { ascending: false });

  if (error) {
    console.warn("[communityAlertsService] getActiveAlerts failed:", error.message);
    return [];
  }

  const now = Date.now();
  return (data || []).filter(a => {
    if (a.auto_expire_at && new Date(a.auto_expire_at).getTime() <= now) return false;
    if (audience === "admin") return true;
    return Array.isArray(a.audience) && a.audience.includes(audience);
  });
}

// ── Service-pause derivation (Phase 3) ─────────────────────────────────────
// The platform is paused when ANY active alert has pauses_service=true.
// No separate flag — the consequence derives from the active Tier 3 alert(s)
// so the two views can never drift. If two Tier 3s are active, lifting one
// leaves the platform paused until the other lifts (intentional).

/**
 * @returns {Promise<boolean>} true if a Tier 3 (pauses_service) alert is
 * currently active. Used by useDriverJobs (skip available-pool fetch),
 * createRequest (block new bookings), and HomePage (swap CTA).
 */
export async function isPlatformPaused() {
  const alert = await getActivePauseAlert();
  return alert !== null;
}

/**
 * @returns {Promise<object|null>} the active Tier 3 alert row (or one of
 * them, if multiple are active — picks the highest-tier-then-most-recent
 * via the ordering already applied in getActiveAlerts). Used by
 * ServicePausedNotice for derived banner copy.
 */
export async function getActivePauseAlert() {
  // Read every active alert visible to the requester (RLS-gated). Filter
  // for pauses_service client-side — alerts hide pauses_service=true
  // alerts from non-targeted audiences via the audience array on the
  // active feed too, but we want to honor pauses_service even when the
  // current viewer's audience role doesn't list it (it's a global state).
  const { data, error } = await supabase
    .from("community_alerts")
    .select("*")
    .is("lifted_at", null)
    .eq("pauses_service", true)
    .order("tier", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) {
    console.warn("[communityAlertsService] getActivePauseAlert failed:", error.message);
    return null;
  }
  const alert = (data || [])[0] || null;
  if (!alert) return null;
  // Honor auto_expire_at client-side (mirrors getActiveAlerts).
  if (alert.auto_expire_at && new Date(alert.auto_expire_at).getTime() <= Date.now()) {
    return null;
  }
  return alert;
}

/**
 * Customer opt-in for service-resume notification (Phase 3 notify-me form
 * on HomePage / BookingFlow). At least one of email/phone must be provided
 * (DB CHECK constraint enforces this). user_id is captured when the
 * customer is signed in; anon opt-ins record null.
 */
export async function createServiceResumeNotification({ alertId, email, phone }) {
  const cleanEmail = (email || "").trim() || null;
  const cleanPhone = (phone || "").trim() || null;
  if (!cleanEmail && !cleanPhone) throw new Error("CONTACT_REQUIRED");
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("service_resume_notifications")
    .insert([{
      alert_id: alertId || null,
      email: cleanEmail,
      phone: cleanPhone,
      user_id: user?.id || null,
    }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Dispatch SMS to all pending opt-ins for a lifted Tier 3 alert. Called
 * from inside liftAlert when the lifted alert was pauses_service=true.
 *
 * Email channel: deferred to Phase 3.5 (no Resend infra wired for arbitrary
 * recipients yet — current send-confirmation only emails the booker). For
 * now, email-only opt-ins are logged and notified_at stamped so they don't
 * accumulate as ghost rows. SMS opt-ins fire via Twilio.
 *
 * Best-effort: per-row failures are logged but don't abort the loop.
 * notified_at is stamped per row inside the success branch.
 */
async function dispatchServiceResumeNotifications(alert) {
  if (!alert || !alert.id) return;
  const { data: rows, error } = await supabase
    .from("service_resume_notifications")
    .select("id, email, phone, alert_id, notified_at")
    .eq("alert_id", alert.id)
    .is("notified_at", null);
  if (error) {
    console.warn("[communityAlertsService] dispatch fetch failed:", error.message);
    return;
  }
  if (!rows || rows.length === 0) return;

  const nowIso = new Date().toISOString();
  for (const row of rows) {
    try {
      if (row.phone) {
        await sendSMSNotification("service_resumed", row.phone, {
          alert_title: alert.title,
        });
      } else if (row.email) {
        // Phase 3.5 follow-up — Resend infrastructure for arbitrary
        // recipients isn't wired yet. Log so the operator knows the
        // opt-in was honored on the lift event, even if no SMS fired.
        console.warn("[communityAlertsService] would have emailed (Phase 3.5):", {
          email: row.email, alertId: alert.id,
        });
      }
      // Stamp notified_at regardless of channel — the lift event was
      // honored; idempotent guard keeps a re-call from re-dispatching.
      await supabase
        .from("service_resume_notifications")
        .update({ notified_at: nowIso })
        .eq("id", row.id);
    } catch (e) {
      console.warn("[communityAlertsService] dispatch row failed (non-fatal):", e?.message);
    }
  }
}

// ── Phase 4: AI co-pilot draft generation ──────────────────────────────────
// Doctrine: "AI suggests. You edit and publish. Nothing leaves until you
// tap publish." This wrapper invokes the draft-community-alert edge fn
// (admin-gated, JWT-verified) which calls Anthropic Claude and returns
// 3 distinct alert drafts. Throws on error so the composer can render
// a friendly message; never persists.

/**
 * Generate 3 alert drafts in different voice flavors.
 * @param {object} payload
 * @param {string} payload.situation - admin's plain-language description
 * @param {0|1|2|3|4} payload.tier   - tone calibration
 * @returns {Promise<{drafts: Array<{flavor:string,title:string,body:string}>}>}
 */
export async function draftCommunityAlert({ situation, tier }) {
  const cleanSituation = (situation || "").toString().trim();
  if (!cleanSituation) throw new Error("SITUATION_REQUIRED");
  if (![0, 1, 2, 3, 4].includes(Number(tier))) throw new Error("INVALID_TIER");

  const { data, error } = await supabase.functions.invoke("draft-community-alert", {
    body: { situation: cleanSituation, tier: Number(tier) },
  });
  if (error) {
    // The edge function returns structured errors; if the body has an
    // `error` field, surface that. Otherwise use the SDK error message.
    const friendly = data?.error || error.message || "Couldn't generate drafts. Try again.";
    throw new Error(friendly);
  }
  if (!data || !Array.isArray(data.drafts) || data.drafts.length !== 3) {
    throw new Error("AI returned an unexpected response. Try again.");
  }
  return data;
}

/**
 * Admin view of every alert ever published (active + lifted), most recent
 * first. Capped to 100 rows. Used by the admin composer's history pane.
 */
export async function getAllAlertsForAdmin({ limit = 100 } = {}) {
  const { data, error } = await supabase
    .from("community_alerts")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) {
    console.warn("[communityAlertsService] getAllAlertsForAdmin failed:", error.message);
    return [];
  }
  return data || [];
}

// ── Write paths ────────────────────────────────────────────────────────────

/**
 * Publish a new community alert. Tier-derived defaults are applied here so
 * components don't have to know about pauses_service / dismissible /
 * auto_expire_at semantics.
 *
 * @param {object} payload
 * @param {0|1|2|3|4} payload.tier
 * @param {string} payload.title
 * @param {string} payload.body
 * @param {string} [payload.reason] - required for Tier 4
 * @param {string[]} [payload.audience] - defaults to tier-appropriate roles
 * @param {Date|string|null} [payload.autoExpireAt] - overrides tier default
 * @returns {Promise<object>} the inserted row
 */
export async function publishAlert(payload) {
  const tier = Number(payload?.tier);
  const cfg = TIERS[tier];
  if (!cfg) throw new Error("INVALID_TIER");

  const title = (payload?.title || "").trim();
  const body = (payload?.body || "").trim();
  if (!title) throw new Error("TITLE_REQUIRED");
  if (!body) throw new Error("BODY_REQUIRED");

  const reason = (payload?.reason || "").trim() || null;
  if (tier === 4 && !reason) throw new Error("REASON_REQUIRED_TIER_4");

  // Resolve auto-expiry: explicit caller value wins, else tier default.
  let autoExpireAt = null;
  if (payload?.autoExpireAt instanceof Date) {
    autoExpireAt = payload.autoExpireAt.toISOString();
  } else if (typeof payload?.autoExpireAt === "string") {
    autoExpireAt = payload.autoExpireAt;
  } else if (cfg.autoExpireHours) {
    autoExpireAt = new Date(Date.now() + cfg.autoExpireHours * 3600 * 1000).toISOString();
  }

  // Audience: caller override or default. Currently both surfaces (resident
  // + driver) get every alert by default; per-tier targeting can ship later.
  const audience = Array.isArray(payload?.audience) && payload.audience.length
    ? payload.audience
    : ["resident", "driver"];

  const { data: { user } } = await supabase.auth.getUser();

  const insertRow = {
    tier,
    title,
    body,
    reason,
    pauses_service: !!cfg.pausesService,
    dismissible: !!cfg.dismissible,
    audience,
    auto_expire_at: autoExpireAt,
    created_by: user?.id || null,
  };

  const { data, error } = await supabase
    .from("community_alerts")
    .insert([insertRow])
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Lift an active alert (mark it lifted_at = now()). Records the lifting
 * admin's user id and an optional reason for audit.
 */
export async function liftAlert(alertId, { reason = null } = {}) {
  if (!alertId) throw new Error("ALERT_ID_REQUIRED");
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("community_alerts")
    .update({
      lifted_at: new Date().toISOString(),
      lifted_by: user?.id || null,
      lifted_reason: (reason || "").trim() || null,
    })
    .eq("id", alertId)
    .is("lifted_at", null)
    .select()
    .single();
  if (error) throw error;

  // Phase 3: when lifting a Tier 3 (service-pause) alert, dispatch SMS
  // to every customer who opted into the notify-me list for this alert.
  // Best-effort; failures are logged but don't unwind the lift itself.
  if (data && data.pauses_service === true) {
    try {
      await dispatchServiceResumeNotifications(data);
    } catch (e) {
      console.warn("[liftAlert] dispatch failed (non-fatal):", e?.message);
    }
  }

  return data;
}
